/**
 * Kundenkonto (P0 Fundament + P1-P4) - gemeinsamer Fetch-Helfer fuer die sitzungsbasierten
 * `/api/public/customer-portal/*`-Endpunkte. Muster wie `lib/api.ts` (apiFetch), aber mit dem
 * Session-Token aus localStorage statt dem Stack-Auth-JWT - diese Seiten liegen bewusst
 * AUSSERHALB des Mitarbeiter-Logins (docs/KONZEPT-KUNDENKONTO.md).
 *
 * `CUSTOMER_SESSION_STORAGE_KEY` lag bisher direkt in `pages/kundenportal-anmelden.tsx` (P0) -
 * hierher verschoben, damit P1+ (Dashboard/Detail) sie nicht aus einer Seiten-Datei importieren
 * muss. `kundenportal-anmelden.tsx` re-exportiert denselben Namen weiter (kein Bruch).
 */
const API_URL = import.meta.env.VITE_API_URL ?? "";
const DEV_TENANT = import.meta.env.VITE_DEV_TENANT;

export const CUSTOMER_SESSION_STORAGE_KEY = "maestro_customer_portal_session";

/** Session-Token aus localStorage - `null` wenn nicht (mehr) vorhanden oder localStorage
 *  blockiert (privates Fenster mit blockiertem Site-Data, P0-Muster). */
export function getCustomerSessionToken(): string | null {
  try {
    return localStorage.getItem(CUSTOMER_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setCustomerSessionToken(token: string): void {
  try {
    localStorage.setItem(CUSTOMER_SESSION_STORAGE_KEY, token);
  } catch {
    // s. P0-Kommentar in kundenportal-anmelden.tsx: die Session bleibt fuer DIESEN Aufruf
    // gueltig, nur ein Neuladen faende sie nicht wieder - kein Blocker.
  }
}

export function clearCustomerSession(): void {
  try {
    localStorage.removeItem(CUSTOMER_SESSION_STORAGE_KEY);
  } catch {
    /* s.o. */
  }
}

/** Geworfen, wenn keine Session vorliegt ODER der Server sie als ungueltig/abgelaufen
 *  zurueckweist (401) - beide Faelle raeumen `localStorage` auf, damit die naechste Seiten-
 *  Pruefung wieder das Login-Formular zeigt statt in einer Fehlerschleife zu haengen. */
export class CustomerSessionExpiredError extends Error {}

/**
 * Fetch mit Session-Bearer-Token. Wirft `CustomerSessionExpiredError`, wenn keine Session
 * vorliegt oder der Server 401 liefert (Session ungueltig/abgelaufen/widerrufen) - der Aufrufer
 * faengt das ab und zeigt das Login-Formular (kein globaler Redirect, diese Seiten sind schon
 * ausserhalb des geschuetzten Bereichs).
 */
export async function customerPortalFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getCustomerSessionToken();
  if (!token) throw new CustomerSessionExpiredError("Keine Sitzung vorhanden.");

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
  if (DEV_TENANT) headers.set("X-Maestro-Tenant", DEV_TENANT);

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearCustomerSession();
    throw new CustomerSessionExpiredError("Sitzung ungültig oder abgelaufen.");
  }
  return res;
}

/** Wandelt eine nicht-OK-Response in einen Error mit `message` (Muster wie `lib/api.ts#toApiError`,
 *  ohne den `code`/`statusCode`-Anteil - die Kundenseiten brauchen bisher nur den Anzeigetext). */
export async function customerPortalApiError(res: Response): Promise<Error> {
  let message = res.statusText;
  try {
    const body = (await res.json()) as { message?: string; error?: string };
    message = body.message ?? body.error ?? message;
  } catch {
    /* ignore */
  }
  return new Error(message);
}
